import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type TouchEvent as ReactTouchEvent,
  type WheelEvent as ReactWheelEvent,
} from 'react';
import { clampViewportScale } from '@ironview/interactive-viewer';

type Point = { x: number; y: number };

interface UseMapViewportResult {
  zoom: number;
  pan: Point;
  isPanning: boolean;
  resetViewport: () => void;
  handleWheel: (event: ReactWheelEvent<HTMLDivElement>) => void;
  handleMouseDown: (event: ReactMouseEvent<HTMLDivElement>) => void;
  handleMouseUp: () => void;
  handleTouchStart: (event: ReactTouchEvent<HTMLDivElement>) => void;
  handleTouchMove: (event: ReactTouchEvent<HTMLDivElement>) => void;
  handleTouchEnd: (event: ReactTouchEvent<HTMLDivElement>) => void;
}

export const useMapViewport = (): UseMapViewportResult => {
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState<Point>({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const dragAnchorRef = useRef<Point | null>(null);
  const touchStartDistanceRef = useRef<number | null>(null);
  const touchStartZoomRef = useRef(1);

  const resetViewport = useCallback(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setIsPanning(false);
    dragAnchorRef.current = null;
    touchStartDistanceRef.current = null;
  }, []);

  const handleWheel = useCallback((event: ReactWheelEvent<HTMLDivElement>) => {
    event.preventDefault();
    const zoomSpeed = 0.1;
    const factor = event.deltaY > 0 ? 1 - zoomSpeed : 1 + zoomSpeed;
    setZoom((prev) => clampViewportScale(prev * factor));
  }, []);

  const handleMouseDown = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      event.preventDefault();
      dragAnchorRef.current = {
        x: event.clientX - pan.x,
        y: event.clientY - pan.y,
      };
      setIsPanning(true);
    },
    [pan.x, pan.y],
  );

  const handleMouseUp = useCallback(() => {
    dragAnchorRef.current = null;
    setIsPanning(false);
  }, []);

  const getTouchDistance = (touches: React.TouchList) => {
    if (touches.length < 2) return 0;
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.hypot(dx, dy);
  };

  const handleTouchStart = useCallback(
    (event: ReactTouchEvent<HTMLDivElement>) => {
      if (event.touches.length === 1) {
        const touch = event.touches[0];
        dragAnchorRef.current = {
          x: touch.clientX - pan.x,
          y: touch.clientY - pan.y,
        };
        setIsPanning(true);
      } else if (event.touches.length >= 2) {
        touchStartDistanceRef.current = getTouchDistance(event.touches);
        touchStartZoomRef.current = zoom;
        dragAnchorRef.current = null;
        setIsPanning(false);
      }
    },
    [pan.x, pan.y, zoom],
  );

  const handleTouchMove = useCallback((event: ReactTouchEvent<HTMLDivElement>) => {
    if (event.touches.length === 1 && dragAnchorRef.current) {
      event.preventDefault();
      const touch = event.touches[0];
      setPan({
        x: touch.clientX - dragAnchorRef.current.x,
        y: touch.clientY - dragAnchorRef.current.y,
      });
    } else if (event.touches.length >= 2 && touchStartDistanceRef.current) {
      event.preventDefault();
      const distance = getTouchDistance(event.touches);
      if (!distance) return;
      const scaleFactor = distance / touchStartDistanceRef.current;
      setZoom(clampViewportScale(touchStartZoomRef.current * scaleFactor));
    }
  }, []);

  const handleTouchEnd = useCallback((event: ReactTouchEvent<HTMLDivElement>) => {
    if (event.touches.length < 2) {
      touchStartDistanceRef.current = null;
    }
    if (event.touches.length === 0) {
      dragAnchorRef.current = null;
      setIsPanning(false);
    } else if (event.touches.length === 1) {
      const touch = event.touches[0];
      dragAnchorRef.current = {
        x: touch.clientX - pan.x,
        y: touch.clientY - pan.y,
      };
      setIsPanning(true);
    }
  }, [pan.x, pan.y]);

  useEffect(() => {
    if (!isPanning) return;

    const handleMouseMove = (event: MouseEvent) => {
      if (!dragAnchorRef.current) return;
      setPan({
        x: event.clientX - dragAnchorRef.current.x,
        y: event.clientY - dragAnchorRef.current.y,
      });
    };

    const stopPanning = () => {
      dragAnchorRef.current = null;
      setIsPanning(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', stopPanning);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', stopPanning);
    };
  }, [isPanning]);

  return {
    zoom,
    pan,
    isPanning,
    resetViewport,
    handleWheel,
    handleMouseDown,
    handleMouseUp,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  };
};
